/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Button,
  HeadingText,
  ParagraphText,
  StackItem,
  StackLayout,
  UtilityText,
  VisuallyHidden,
  styled,
  Image,
  GDSIconSource,
  Tag,
  GDSColorToken,
  GDSTagProps,
} from '@leagueplatform/genesis-core';
import { type MasonryEngineNodeRendererProps } from '@leagueplatform/masonry-engine';
import { InternalOrExternalLink } from '@leagueplatform/web-common-components';
import { PageHeaderBackground } from '@leagueplatform/masonry-widgets';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import { MasonryEngineNode } from '@leagueplatform/masonry-engine';
import {
  MasonryDeeplinkAction,
  MasonryDriverAction,
} from '#types/masonry-driver-node-actions.ts';
import { mapIconTintToken } from '#utils/map-icon-tint-token.ts';
import { formatClassNames } from '#utils/format-masonry-class-name.util.ts';
import { useInvokeActionWithLoadingState } from '../../hooks/use-invoke-action-with-loading-state.hook';
import { useMasonryHeroBannerHeight } from '../../hooks/use-masonry-hero-banner-height.hook';

type MasonryHeroBannerNodeProperties = {
  heading?: string;
  body?: string;
  eyebrow?: string;
  buttonLabel?: string;
  buttonIcon?: GDSIconSource;
  image?: string;
  imageAltText?: string;
  icon?: string;
  curved?: boolean;
  includeSafeArea?: boolean;
  tags?: Array<
    Omit<GDSTagProps, 'iconTint'> & {
      iconTint?: GDSColorToken;
    }
  >;
  imageMode?: 'fit' | 'cover';
};

export type MasonryHeroBannerNodeActions = {
  onButtonClick?: MasonryDriverAction;
};

export type MasonryHeroBannerNode = MasonryEngineNode<
  'heroBanner',
  MasonryHeroBannerNodeProperties,
  MasonryHeroBannerNodeActions
>;

export type MasonryHeroBannerRendererProps =
  MasonryEngineNodeRendererProps<MasonryHeroBannerNode>;

const DecorativeIcon = styled(Image, {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const MasonryHeroBannerRenderer = ({
  css,
  heading,
  body,
  eyebrow,
  buttonLabel,
  buttonIcon,
  image,
  imageAltText = '',
  imageMode,
  icon,
  curved,
  includeSafeArea = false,
  tags,
  onButtonClick,
  className,
  engineClassName,
  locatorId,
  rootElementRef,
}: MasonryHeroBannerRendererProps) => {
  const { height, pageHeaderBackgroundHeight, contentRef } =
    useMasonryHeroBannerHeight();
  const { invokeAction, isLoading: isClickInFlight } =
    useInvokeActionWithLoadingState();

  const buttonHref = (onButtonClick as MasonryDeeplinkAction)?.payload.url;

  const mappedTags = tags?.map(mapIconTintToken);

  return (
    <PageHeaderBackground
      ref={rootElementRef}
      height={height}
      pageHeaderBackgroundHeight={pageHeaderBackgroundHeight}
      backgroundImage={image}
      curved={curved}
      imageAltText={imageAltText}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      imageMode={imageMode}
    >
      <StackLayout
        orientation="horizontal"
        spacing="$one"
        horizontalAlignment="start"
        verticalAlignment="top"
        css={{ position: 'relative', ...css }}
      >
        <StackItem
          horizontalAlignment="start"
          css={{
            marginInlineEnd: includeSafeArea ? '160px' : '0',
            flex: 1,
          }}
          verticalAlignment="top"
          ref={contentRef}
        >
          <StackLayout
            orientation="horizontal"
            spacing="$oneAndHalf"
            horizontalAlignment="start"
            verticalAlignment="top"
            css={{ width: '100%' }}
          >
            {icon && (
              <StackItem
                css={{
                  flex: '1 1 100%',
                  overflow: 'hidden',
                  aspectRatio: '1 / 1',
                  maxWidth: '64px',
                  '@mobile': {
                    order: 2,
                  },
                  '@laptop': {
                    maxWidth: '80px',
                  },
                  '@desktop': {
                    maxWidth: '96px',
                  },
                }}
              >
                <DecorativeIcon src={icon} alt="" data-testid="hero-icon" />
              </StackItem>
            )}
            <StackItem horizontalAlignment="start" verticalAlignment="top">
              {mappedTags && mappedTags.length > 0 && (
                <StackLayout
                  orientation="horizontal"
                  spacing="$half"
                  css={{ flexWrap: 'wrap' }}
                >
                  {mappedTags.map((tag, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StackItem growFactor={0} key={`${tag.label} - ${index}`}>
                      <Tag {...tag} />
                    </StackItem>
                  ))}
                </StackLayout>
              )}
              {eyebrow && (
                <UtilityText
                  size="eyebrow"
                  css={{
                    marginTop: mappedTags?.length ? '$half' : undefined,
                  }}
                >
                  {eyebrow}
                </UtilityText>
              )}
              {heading && (
                <HeadingText
                  level="1"
                  size={{
                    '@initial': 'xl',
                    '@laptop': 'xxl',
                    '@desktop': 'xxl',
                  }}
                  css={{
                    marginTop: '$quarter',
                  }}
                >
                  {heading}
                </HeadingText>
              )}

              {body && (
                <ParagraphText
                  htmlStringOptions={BodyTextHtmlTagOptions}
                  css={{
                    typography: '$bodyTwo',
                    marginTop: '$threeQuarters',
                    color: '$onSurfaceTextPrimary',
                    '@laptop': {
                      typography: '$bodyOne',
                    },
                    '@desktop': {
                      typography: '$bodyOne',
                    },
                  }}
                >
                  {body}
                </ParagraphText>
              )}
              {buttonLabel && (
                <Button
                  size="medium"
                  priority="primary"
                  icon={buttonIcon}
                  css={{
                    marginTop: '$one',
                  }}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(buttonHref && {
                    as: InternalOrExternalLink,
                    disableDefaultOnClick: true,
                  })}
                  href={buttonHref}
                  onClick={(event: React.MouseEvent) => {
                    if (onButtonClick) {
                      invokeAction({
                        event,
                        ...onButtonClick,
                      });
                    }
                  }}
                  // if the cta is not a url, we need to show the loading and disabled states
                  loading={buttonHref ? undefined : isClickInFlight}
                  disabled={buttonHref ? undefined : isClickInFlight}
                >
                  {buttonLabel}
                </Button>
              )}
              {image && (
                <VisuallyHidden
                  data-testid="hero-img"
                  role="img"
                  aria-label={imageAltText}
                />
              )}
            </StackItem>
          </StackLayout>
        </StackItem>
      </StackLayout>
    </PageHeaderBackground>
  );
};
